import {Component, DestroyRef, DoCheck, OnInit, ViewChild} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {CdkDrag, CdkDragHandle, DragRef, Point} from '@angular/cdk/drag-drop';
import {CommonModule} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {NgSelectModule} from '@ng-select/ng-select';
import {PlanModelService} from '../../../common/service/plan-model.service';
import {LookupService} from '../../../common/service/lookup.service';
import {ExportPlanService} from '../../../common/service/export-plan.service';
import {AlertModalService} from '../../../common/component/modal/alert-modal/alert-modal.service';
import {ConfirmationModalService} from '../../../common/component/modal';
import {WidgetService} from '../../services/widget.service';
import {FileType} from '../../../common/models/design-properties.model';
import {NgEventBus} from 'ng-event-bus';
import {AppConfigService} from '../../../common/service/app-config.service';
import {SpatialUtilService} from '../../services/spatial-util.service';
import {HelpLinkComponent} from '../../../help/help-link.component';
import {HELP_LINK_CONTENTS_BASE_URL} from '../../../common/constants';
import {concatMap, EMPTY, tap} from 'rxjs';

export interface widgetConfig {
    header?: string;
    closeTooltip?: string;
    collapseTooltip?: string;
    expandTooltip?: string;
}

export interface fileTypeSelected {
    value: FileType;
}

@Component({
    selector: 'nlr-export-survey-data',
    imports: [CommonModule, HelpLinkComponent, FormsModule, CdkDrag, CdkDragHandle, NgSelectModule],
    templateUrl: './export-survey-data.component.html',
    styleUrl: './export-survey-data.component.scss'
})
export class ExportSurveyDataComponent implements OnInit, DoCheck {
    @ViewChild('widget')
    widgetElement: HTMLElement;

    title: string = 'ExportSurveyDataController';

    widgetId: string = 'exportSurveyData';

    widgetConfig: widgetConfig = undefined;

    open: boolean = true;

    displayProjection: boolean = false;

    processing: boolean = false;

    fileTypes: FileType[];

    fileTypeSelected: fileTypeSelected;

    selectedFileType: string;

    helpLinkUrl: string = HELP_LINK_CONTENTS_BASE_URL + 'export-help.html';

    helpPageTitle: string = 'Export Survey Data Tool';

    widgetVisibleAtLastCheck: boolean;

    isWidgetHidden = true;

    constructor(
        private planModelService: PlanModelService,
        private lookupService: LookupService,
        private exportPlanService: ExportPlanService,
        private eventBus: NgEventBus,
        private alertModalService: AlertModalService,
        private confirmationModalService: ConfirmationModalService,
        private appConfigService: AppConfigService,
        private spatialUtilService: SpatialUtilService,
        private destroyRef: DestroyRef,
        private widgetService: WidgetService
    ) {
        if (this.spatialUtilService.hasEdits()) {
            this.confirmationModalService
                .showModal({
                    title: 'Save plan?',
                    confirmationText: 'The plan must be saved before it can be exported.<br>Do you want to save the plan?',
                    iconType: 'info'
                })
                .pipe(
                    tap((action: string) => {
                        if (action === 'yes') {
                            this.saveFeatures();
                        }
                    }),
                    takeUntilDestroyed(this.destroyRef)
                )
                .subscribe();
        } else {
            this.isWidgetHidden = false;
        }
    }

    ngOnInit(): void {
        console.debug(this.title + ' : initiated');
        this.fileTypes = this.appConfigService.getDesignProperties().DESIGN_WIDGETS_CONFIGURATION.IMPORT_SURVEY_DATA.FILE_TYPES;
        this.fileTypeSelected = {value: this.appConfigService.getDesignProperties().DESIGN_WIDGETS_CONFIGURATION.IMPORT_SURVEY_DATA.FILE_TYPES[0]};
        this.selectedFileType = this.fileTypeSelected.value.FILE_TYPE;
        this.widgetConfig = this.lookupService.getLookups('widgetConfig').find(l => l.name === this.widgetId);
        this.eventBus.cast('nlr:export-survey-data:traitsChanged', this.widgetId);
    }

    saveFeatures() {
        const subscription = this.spatialUtilService.save().subscribe({
            complete: () => {
                this.isWidgetHidden = false;
            }
        });
        this.eventBus.cast('nlr:busy', subscription);
    }

    ngDoCheck() {
        if (this.widgetElement && !this.widgetVisibleAtLastCheck) {
            this.widgetService.setupWidget(this.widgetId);
        }
        this.widgetVisibleAtLastCheck = !!this.widgetElement;
    }

    computeDragPosition = (userPointerPosition: Point, dragRef: DragRef, dimensions: DOMRect): Point => {
        return this.widgetService.computeDragPosition(userPointerPosition, dragRef, dimensions);
    };

    fileTypeChange() {
        console.debug(this.title + ' : fileTypeChange');
        this.displayProjection = this.fileTypeSelected.value.PROJECTION_REQUIRED ? true : false;
    }

    isPlanSaved() {
        return this.planModelService.getModel().id !== undefined && this.planModelService.getModel().id !== null;
    }

    submit() {
        console.debug(this.title + ' : submit');
        if (this.selectedFileType === 'CSD') {
            const subscription = this.exportPlanService
                .validateInput(this.selectedFileType)
                .pipe(
                    concatMap(isValid => {
                        if (isValid) {
                            return this.exportPlanService.generateCSD();
                        }
                        return EMPTY;
                    }),
                    takeUntilDestroyed(this.destroyRef)
                )
                .subscribe();

            this.eventBus.cast('nlr:busy', subscription);
        } else {
            this.alertModalService.createAlertMessage(this.selectedFileType + ' export is not implemented.', 'OK', 'Warning');
        }
    }

    updateSelectedFileType(fileType: FileType) {
        this.selectedFileType = fileType.FILE_TYPE;
    }

    toggleWidgetVisibility() {
        this.widgetService.onClick('ExportSurveyData');
    }

    isWidgetActive() {
        return this.widgetService.isWidgetActive('ExportSurveyData');
    }

    toggleWidgetContentVisibility() {
        this.widgetService.showOrHideWidgetContent('exportSurveyData', this.open);
        this.open = !this.open;
    }
}
